import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ContentCard from '@/components/feature/ContentCard';
import EmptyState from '@/components/base/EmptyState';
import { useSEO } from '@/hooks/useSEO';
import { searchContent, getHotItems, type ContentItem } from '@/hooks/useContent';

const TABS: { key: 'all' | ContentItem['type']; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'news', label: '资讯' },
  { key: 'video', label: '视频' },
  { key: 'topic', label: '话题' },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get('q') || '';
  const [draft, setDraft] = useState(keyword);
  const [tab, setTab] = useState<'all' | ContentItem['type']>('all');

  useSEO(
    keyword ? `“${keyword}”的搜索结果 - 一起艹` : '搜索 - 一起艹',
    keyword
      ? `一起艹为你找到与“${keyword}”相关的娱乐资讯、视频与互动话题，快速定位你感兴趣的内容。`
      : '在一起艹搜索娱乐资讯、视频与互动话题，输入关键词即可快速找到相关内容。',
    keyword ? `${keyword},搜索,娱乐,一起艹` : '搜索,娱乐资讯,视频,话题,一起艹',
  );

  const results = useMemo(() => searchContent(keyword), [keyword]);
  const filtered = useMemo(
    () => (tab === 'all' ? results : results.filter((item) => item.type === tab)),
    [results, tab],
  );
  const hotItems = getHotItems(3);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = draft.trim();
    setParams(value ? { q: value } : {});
  };

  return (
    <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />

      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-3xl">
        <div className="flex items-center gap-3 flex-1 h-12 px-4 rounded-md border border-background-200 bg-background-50 focus-within:border-primary-400">
          <i className="ri-search-line text-lg text-primary-500" />
          <input
            type="text"
            name="q"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="搜索资讯、视频、话题关键词…"
            className="flex-1 bg-transparent text-sm md:text-base text-foreground-950 placeholder:text-foreground-400 outline-none"
          />
        </div>
        <button
          type="submit"
          className="h-12 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors"
        >
          搜索
        </button>
      </form>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-sm text-foreground-500">
          {keyword ? (
            <>
              关键词“<span className="text-primary-600 font-medium">{keyword}</span>”共找到{' '}
              <span className="text-foreground-950 font-medium">{results.length}</span> 条结果
            </>
          ) : (
            '请输入关键词，搜索资讯、视频与互动话题'
          )}
        </p>
        {keyword ? (
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 w-fit">
            {TABS.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setTab(option.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  tab === option.key
                    ? 'bg-secondary-700 text-background-50'
                    : 'text-foreground-600 hover:text-secondary-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="mt-8">
        {!keyword ? (
          <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-4">
              <i className="ri-search-eye-line text-3xl" />
            </div>
            <h2 className="font-heading text-lg md:text-xl text-foreground-950">输入关键词开始探索</h2>
            <p className="mt-2 text-sm text-foreground-500">试试搜索「音乐节」「短剧」「现场演出」等热门关键词。</p>
          </div>
        ) : filtered.length === 0 ? (
          <div>
            <EmptyState
              icon="ri-search-eye-line"
              title="没有找到相关结果"
              description="换个关键词试试，或者看看下面的热门内容推荐。"
              actionLabel="返回首页"
              onAction={() => {
                window.REACT_APP_NAVIGATE?.('/');
              }}
            />
            <div className="mt-8">
              <h3 className="font-heading text-lg text-foreground-950 mb-4">热门内容推荐</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {hotItems.map((item) => (
                  <ContentCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filtered.map((item) => (
              <ContentCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      <p className="mt-10 text-xs text-foreground-400 text-center">
        找不到想要的内容？可以<Link to="/about" className="text-primary-600 hover:underline">联系我们</Link>告诉我们。
      </p>
    </div>
  );
}